import {
  get,
  isArray,
  conformsTo,
  isString,
  isNumber,
  isDate,
  floor,
} from 'lodash';
import uuid4 from 'uuid/v4';

import { NutritionCodeEnum } from 'Common/Constants/Enumeration/NutritionCodeEnum';
import { TransactionEnum, StudentProfileEnum } from 'Common/FieldEnum';
import { add, subtract, percentage } from 'Common/Helpers/currency';

export const hasAmountPaid = (transaction) => {
  const { AmountPaid } = transaction;
  return Number(AmountPaid) > 0;
};

export const hasNegativeAmountPaid = (transaction) => {
  const { AmountPaid } = transaction;
  return Number(AmountPaid) < 0;
};

export const getAmountPaid = (transaction) => {
  return Number(get(transaction, TransactionEnum.AmountPaid, 0));
};

export const getChange = (amountPaid, cost, balance) => {
  if (amountPaid < 0) {
    if (cost <= balance) {
      return Math.abs(amountPaid);
    }
    if (cost > balance) {
      return subtract(0, cost);
    }
  }
  return subtract(amountPaid, cost);
};

export const getCost = (subtotal, tax) => {
  return add(subtotal, tax);
};

export const getSubtotal = (boughtItems) => {
  return boughtItems.reduce((acc, item) => {
    return add(acc, item.Price);
  }, 0);
};

export const getTaxRate = (profile, taxRatesByStatus) => {
  const taxRate = taxRatesByStatus.find(
    (x) => x.Status === profile[StudentProfileEnum.StatusCurrent]
  );
  return taxRate ? taxRate.TaxRate : 0;
};

/**
 * Calculate tax for a subtotal
 * Calculates tax up to 4 decimal places then round to 2 decimal places
 * @param {Number} subtotal
 * @param {Number} taxRate
 * @example
 * getTax(4.90, 5) // 0.25
 * getTax(5.55, 10) // 0.56
 * getTax(20.11, 3) // 0.6
 */
export const getTax = (subtotal, taxRate) => {
  return taxRate && subtotal > 0
    ? floor(percentage(subtotal, taxRate, 4), 2)
    : 0;
};

export const hasBoughtItems = (transaction) => {
  const { BoughtItems } = transaction;
  return isArray(BoughtItems) && BoughtItems.length > 0;
};

export const getBoughtItems = (transaction) => {
  return get(transaction, TransactionEnum.BoughtItems, []);
};

export const hasCheckNumber = (transaction) => {
  const { CheckNumber } = transaction;
  return CheckNumber ? CheckNumber.length > 0 : false;
};

export const hasMultipleReimbursableMeals = (transaction) => {
  let reimbursableMealCount = 0;
  transaction.BoughtItems.forEach((item) => {
    if (item.NutritionCode === NutritionCodeEnum.FederalType1Meal) {
      reimbursableMealCount += item.Qty;
    }
  });
  return reimbursableMealCount > 1;
};

export const getTotalReimbursableAmount = (transaction) =>
  getBoughtItems(transaction).reduce((amount, item) => {
    if (item.NutritionCode === NutritionCodeEnum.FederalType1Meal) {
      return add(amount, item.Price);
    }
    return amount;
  }, 0);

export const getPendingAccountBalance = (balance, cost) => {
  return subtract(balance, cost);
};

export const getBalance = (profile) => Number(get(profile, 'Balance', 0));

const isValidBoughtItems = (BoughtItems) => {
  return (
    isArray(BoughtItems) && !BoughtItems.some((item) => Number(item.Price) < 0)
  );
};

/**
 * Add extra properties to create a valid transaction request.
 * Will not overwrite properties that already exist.
 * @param {Object} transaction
 * @param {String} transaction.PrimarySiteId
 * @param {String} transaction.MealType
 * @param {Number} transaction.StudentIdSeed
 * @param {Number} transaction.TransactionType
 * @param {String|Date} transaction.WorkingDT
 * @param {Number} transaction.AmountPaid
 * @param {String} [transaction.CheckNumber]
 * @param {Object[]} transaction.BoughtItems
 * @param {Number} transaction.ServerId
 * @return {Transaction}
 */
export const buildTransactionRequest = (transaction) => {
  return { ServerId: 0, TransactionGuid: uuid4(), ...transaction };
};

/**
 * @param {Object} transaction
 * @param {String} transaction.PrimarySiteId
 * @param {String} transaction.MealType
 * @param {Number} transaction.StudentIdSeed
 * @param {Number} transaction.TransactionType
 * @param {String|Date} transaction.WorkingDT
 * @param {Number} transaction.AmountPaid
 * @param {String} [transaction.CheckNumber]
 * @param {Object[]} transaction.BoughtItems
 * @param {Number} transactionDetails.ServerId
 * @param {string} transaction.TransactionGuid
 * @return {Boolean}
 */
export const isValidTransaction = (transaction) =>
  conformsTo(transaction, {
    PrimarySiteId: isString,
    MealType: isString,
    StudentIdSeed: (v) => isNumber(v) || isString(v),
    WorkingDT: (v) => isString(v) || isDate(v),
    AmountPaid: (v) => isNumber(v) || isString(v),
    BoughtItems: isValidBoughtItems,
    TransactionType: isNumber,
    ServerId: (v) => isNumber(v),
    TransactionGuid: (v) => isString(v) && v.length === 36, //RFC4122 v4 UUID
  });



// WEBPACK FOOTER //
// ./src/pages/pointofsale/helpers/transactions.js